import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Modal,
    Button,
    Radio,
    Space,
    Typography,
    Divider,
    message,
    Dropdown,
    Menu,
    DatePicker,
    TimePicker
} from 'antd';
import { FileExcelOutlined, FileTextOutlined, DownloadOutlined, DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DATE_PRESETS, applyPreset } from './datePresets';

const { Text } = Typography;

// Extended date presets

const EXTENDED_DATE_PRESETS = [
    {
        key: 'thisQuarter',
        label: 'This Quarter',
        range: () => {
            const current = dayjs();
            const currentMonth = current.month();
            // Calculate start of quarter based on current month
            // Jan-Mar: Q1, Apr-Jun: Q2, Jul-Sep: Q3, Oct-Dec: Q4
            const quarterStartMonth = Math.floor(currentMonth / 3) * 3;

            return [
                dayjs().month(quarterStartMonth).startOf('month').startOf('day'),
                current.endOf('day')
            ];
        }
    },
    {
        key: 'lastQuarter',
        label: 'Last Quarter',
        range: () => {
            const current = dayjs();
            const currentMonth = current.month();
            // Calculate the start month of the current quarter
            const thisQuarterStartMonth = Math.floor(currentMonth / 3) * 3;

            // Calculate the start month of the previous quarter
            let lastQuarterStartMonth = thisQuarterStartMonth - 3;
            let lastQuarterYear = current.year();

            // Handle year boundary for Q1
            if (lastQuarterStartMonth < 0) {
                lastQuarterStartMonth += 12; // Adjust to previous year's Q4
                lastQuarterYear -= 1;
            }

            // Calculate the end month of the previous quarter
            const lastQuarterEndMonth = lastQuarterStartMonth + 2;

            return [
                dayjs().year(lastQuarterYear).month(lastQuarterStartMonth).startOf('month').startOf('day'),
                dayjs().year(lastQuarterYear).month(lastQuarterEndMonth).endOf('month').endOf('day')
            ];
        }
    },
    {
        key: 'thisYear',
        label: 'This Year',
        range: () => [
            dayjs().startOf('year').startOf('day'),
            dayjs().endOf('day')
        ]
    },
    {
        key: 'lastYear',
        label: 'Last Year',
        range: () => [
            dayjs().subtract(1, 'year').startOf('year').startOf('day'),
            dayjs().subtract(1, 'year').endOf('year').endOf('day')
        ]
    }
];

// Combined presets for dropdown
const ALL_DATE_PRESETS = [...DATE_PRESETS, ...EXTENDED_DATE_PRESETS];

const ExtendedExportModal = ({
                                 visible,
                                 onClose,
                                 currentConfig,
                                 currentDateRange,
                                 exportType,
                                 onSyncConfig,
                                 onRequestExport
                             }) => {
    // State for selected options
    const [exportFormat, setExportFormat] = useState('csv');
    const [exportDataTypes, setExportDataTypes] = useState([]);
    const [isExporting, setIsExporting] = useState(false);

    // Date state management
    const [selectedDatePreset, setSelectedDatePreset] = useState(null);
    const [dateRange, setDateRange] = useState(currentDateRange || {
        start: new Date(),
        end: new Date()
    });


    // Reset state when modal becomes visible
    useEffect(() => {
        if (visible) {
            // Set default date range from current range
            setDateRange(currentDateRange || {
                start: new Date(),
                end: new Date()
            });

            // Reset date preset to show "Current Dashboard Range" by default
            setSelectedDatePreset('current');

            // Set default export type based on what launched the modal
            if (exportType) {
                setExportDataTypes(
                    exportType === 'both'
                        ? ['calls', 'scorecard']
                        : [exportType]
                );
            } else {
                setExportDataTypes([]);
            }
        }
    }, [visible, currentDateRange, exportType]);

    // Handle date preset selection
    const handleDatePresetChange = useCallback((presetKey) => {
        // Update the selected preset
        setSelectedDatePreset(presetKey);

        if (presetKey === 'current') {
            // Use current dashboard range
            setDateRange(currentDateRange);
            return;
        }

        const preset = ALL_DATE_PRESETS.find(p => p.key === presetKey);
        if (preset) {
            const [start, end] = preset.range();
            setDateRange({
                start: start.toDate(),
                end: end.toDate()
            });
        }
    }, [currentDateRange]);

    // Handle export data type toggle (calls/scorecards)
    const handleDataTypeChange = useCallback((type) => {
        setExportDataTypes(prev => {
            const newTypes = [...prev];
            const index = newTypes.indexOf(type);

            if (index > -1) {
                // Remove if already present
                newTypes.splice(index, 1);
            } else {
                // Add if not present
                newTypes.push(type);
            }

            return newTypes;
        });
    }, []);

    // Handle export format change
    const handleFormatChange = useCallback((e) => {
        setExportFormat(e.target.value);
    }, []);

    const handleStartDateChange = (date) => {
        setSelectedDatePreset('custom'); // Set to custom when dates are changed manually

        // Get time from existing start date
        const existingTime = dayjs(dateRange.start);

        // Create a new date with the selected date and existing time
        const newStart = date
            .hour(existingTime.hour())
            .minute(existingTime.minute())
            .second(0)
            .toDate();

        // Update the date range directly
        setDateRange(prev => ({
            ...prev,
            start: newStart
        }));
    };

    const handleEndDateChange = (date) => {
        setSelectedDatePreset('custom'); // Set to custom when dates are changed manually

        // Get time from existing end date
        const existingTime = dayjs(dateRange.end);

        // Create a new date with the selected date and existing time
        const newEnd = date
            .hour(existingTime.hour())
            .minute(existingTime.minute())
            .second(59)
            .toDate();

        // Update the date range directly
        setDateRange(prev => ({
            ...prev,
            end: newEnd
        }));
    };

    const handleStartTimeChange = (time) => {
        setSelectedDatePreset('custom'); // Set to custom when times are changed manually

        // Get date from existing start date
        const existingDate = dayjs(dateRange.start);

        // Create a new date with existing date and selected time
        const newStart = existingDate
            .hour(time.hour())
            .minute(time.minute())
            .second(0)
            .toDate();

        // Update the date range directly
        setDateRange(prev => ({
            ...prev,
            start: newStart
        }));
    };

    const handleEndTimeChange = (time) => {
        setSelectedDatePreset('custom'); // Set to custom when times are changed manually

        // Get date from existing end date
        const existingDate = dayjs(dateRange.end);

        // Create a new date with existing date and selected time
        const newEnd = existingDate
            .hour(time.hour())
            .minute(time.minute())
            .second(59)
            .toDate();

        // Update the date range directly
        setDateRange(prev => ({
            ...prev,
            end: newEnd
        }));
    };


    const displayStartDate = dayjs(dateRange.start);
    const displayEndDate = dayjs(dateRange.end);
    const displayStartTime = dayjs(dateRange.start);
    const displayEndTime = dayjs(dateRange.end);

    // Handle the export action
    const handleExport = useCallback(async () => {
        // Validate export types
        if (exportDataTypes.length === 0) {
            message.warning('Please select at least one data type to export');
            return;
        }

        try {
            // First make sure we have the latest config state
            if (onSyncConfig) {
                onSyncConfig();
            }

            setIsExporting(true);

            // Create the export request payload
            const exportPayload = {
                config: {
                    ...currentConfig,
                    // Override the date range with the one selected in the modal
                    dateRange: {
                        start: dateRange.start.toISOString(),
                        end: dateRange.end.toISOString()
                    }
                },
                exportTypes: exportDataTypes,
                format: exportFormat
            };

            // Call the parent component's handler for the export request
            await onRequestExport(exportPayload);

            message.success('Export request submitted. You will be notified when it\'s ready to download.');
            onClose();
        } catch (error) {
            console.error('Error requesting export:', error);
            message.error('Failed to request export');
        } finally {
            setIsExporting(false);
        }
    }, [onSyncConfig, currentConfig, dateRange, exportDataTypes, exportFormat, onRequestExport, onClose]);

    // Create the date preset dropdown menu
    const datePresetMenu = (
        <Menu onClick={({ key }) => handleDatePresetChange(key)}>
            <Menu.Item key="current">Current Dashboard Range</Menu.Item>
            <Menu.Divider />
            {ALL_DATE_PRESETS.map(preset => (
                <Menu.Item key={preset.key}>{preset.label}</Menu.Item>
            ))}
        </Menu>
    );

    // Get the currently selected preset label
    const getSelectedPresetLabel = () => {
        if (selectedDatePreset === 'current') return 'Current Dashboard Range';
        if (selectedDatePreset === 'custom') return 'Custom Range';

        const preset = ALL_DATE_PRESETS.find(p => p.key === selectedDatePreset);
        return preset ? preset.label : 'Select Range';
    };

    // Memoize the footer buttons to prevent recreating on every render
    const modalFooter = useMemo(() => [
        <Button key="cancel" onClick={onClose}>
            Cancel
        </Button>,
        <Button
            key="export"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={isExporting}
            disabled={exportDataTypes.length === 0}
        >
            Request Export
        </Button>
    ], [onClose, handleExport, isExporting, exportDataTypes.length]);

    return (
        <Modal
            title="Export Extended Data Report"
            visible={visible}
            onCancel={onClose}
            footer={modalFooter}
            width={520}
            destroyOnClose={true}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Export Format Section */}
                <div>
                    <Text style={{ fontSize: '16px', fontWeight: 500, display: 'block', marginBottom: '16px', color: '#111827' }}>
                        File Format
                    </Text>
                    <Radio.Group
                        value={exportFormat}
                        onChange={handleFormatChange}
                        style={{ display: 'flex', gap: '16px' }}
                    >
                        <Radio.Button value="csv" style={{ height: '40px', display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                            <Space align="center">
                                <FileExcelOutlined style={{ fontSize: '16px', color: '#10b981' }}/>
                                <span>CSV</span>
                            </Space>
                        </Radio.Button>
                        <Radio.Button value="json" style={{ height: '40px', display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                            <Space align="center">
                                <FileTextOutlined style={{ fontSize: '16px', color: '#3b82f6' }}/>
                                <span>JSON</span>
                            </Space>
                        </Radio.Button>
                    </Radio.Group>
                </div>

                <Divider style={{ margin: '0' }} />

                {/* Data Types Section - New Button Style */}
                <div>
                    <Text style={{ fontSize: '16px', fontWeight: 500, display: 'block', marginBottom: '16px', color: '#111827' }}>
                        Data to Export
                    </Text>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <Button
                            type={exportDataTypes.includes('calls') ? 'primary' : 'default'}
                            onClick={() => handleDataTypeChange('calls')}
                            style={{
                                flex: 1,
                                height: '40px',
                                borderColor: exportDataTypes.includes('calls') ? '#10b981' : undefined,
                                backgroundColor: exportDataTypes.includes('calls') ? '#10b981' : undefined,
                            }}
                        >
                            Calls
                        </Button>
                        <Button
                            type={exportDataTypes.includes('scorecard') ? 'primary' : 'default'}
                            onClick={() => handleDataTypeChange('scorecard')}
                            style={{
                                flex: 1,
                                height: '40px',
                                borderColor: exportDataTypes.includes('scorecard') ? '#10b981' : undefined,
                                backgroundColor: exportDataTypes.includes('scorecard') ? '#10b981' : undefined,
                            }}
                        >
                            Scorecards
                        </Button>
                    </div>
                </div>

                <Divider style={{ margin: '0' }} />

                {/* Date Range Section */}
                <div>
                    <Text style={{ fontSize: '16px', fontWeight: 500, display: 'block', marginBottom: '16px', color: '#111827' }}>
                        Date Range
                    </Text>

                    {/* Date Preset Pill/Dropdown */}
                    <div style={{ marginBottom: '16px' }}>
                        <Dropdown overlay={datePresetMenu} trigger={['click']}>
                            <Button
                                style={{
                                    borderRadius: '20px',
                                    backgroundColor: '#10b981',
                                    borderColor: '#10b981',
                                    color: 'white',
                                    padding: '0 16px'
                                }}
                            >
                                {getSelectedPresetLabel()} <DownOutlined style={{ fontSize: '12px', marginLeft: '4px' }} />
                            </Button>
                        </Dropdown>
                    </div>
                    {/* Date and Time Inputs - Vertical Layout */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '1px solid #e5e7eb'
                    }}>
                        {/* Start Date/Time */}
                        <div>
                            <Text style={{ color: '#6b7280', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                                Start Date and Time:
                            </Text>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <DatePicker
                                    value={displayStartDate}
                                    onChange={handleStartDateChange}
                                    allowClear={false}
                                    format="YYYY-MM-DD"
                                    style={{ flex: 2 }}
                                />
                                <TimePicker
                                    value={displayStartTime}
                                    onChange={handleStartTimeChange}
                                    format="HH:mm"
                                    minuteStep={5}
                                    allowClear={false}
                                    style={{ flex: 1 }}
                                />
                            </div>
                        </div>

                        {/* End Date/Time */}
                        <div>
                            <Text style={{ color: '#6b7280', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                                End Date and Time:
                            </Text>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <DatePicker
                                    value={displayEndDate}
                                    onChange={handleEndDateChange}
                                    allowClear={false}
                                    format="YYYY-MM-DD"
                                    style={{ flex: 2 }}
                                />
                                <TimePicker
                                    value={displayEndTime}
                                    onChange={handleEndTimeChange}
                                    format="HH:mm"
                                    minuteStep={5}
                                    allowClear={false}
                                    style={{ flex: 1 }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #bae6fd',
                    marginTop: '16px'
                }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <div style={{
                            backgroundColor: '#0ea5e9',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}>
                            i
                        </div>
                        <div>
                            <Text style={{ fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                                Extended Range Report
                            </Text>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Extended date range reports do not support all dashboard features. Some fields and filters may be omitted.
                            </Text>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

// Export as memoized component to prevent unnecessary re-renders
export default React.memo(ExtendedExportModal);
